import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ClientEncoderResponse, CacheInfo } from '@shared/types';
import { API_BASE_URL } from '@/client/config';
import { handleApiError } from '@/client/lib/apiError';


export interface EncoderWithClient extends ClientEncoderResponse {
  clientName: string;
}

interface EncodersState {
  encoders: EncoderWithClient[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  cacheInfo: CacheInfo | null;
  setEncoders: (encoders: EncoderWithClient[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  fetchEncoders: (accessToken: string, logout: () => void, forceRefresh?: boolean) => Promise<void>;
  getEncoder: (encoderID: string) => EncoderWithClient | undefined;
  invalidateCache: () => void;
}

export const useEncodersStore = create<EncodersState>()(devtools((set, get) => ({
  encoders: [],
  loading: false,
  error: null,
  lastFetched: null,
  cacheInfo: null,
  setEncoders: (encoders) => set({ encoders, error: null }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  getEncoder: (encoderID) => get().encoders.find(e => e.encoderID === encoderID),
  invalidateCache: () => set({ encoders: [], lastFetched: null, cacheInfo: null }),
  fetchEncoders: async (accessToken: string, logout: () => void, forceRefresh = false) => {
    const state = get();

    // If we have cached data and not forcing refresh, skip the fetch
    if (!forceRefresh && state.encoders.length > 0 && state.lastFetched) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/api/encoders`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (handleApiError(response, logout)) return;
        throw new Error(`Failed to fetch encoders: ${response.statusText}`);
      }

      const data = await response.json();
      set({
        encoders: data.encoders,
        lastFetched: Date.now(),
        loading: false,
        cacheInfo: data.cacheInfo ?? null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      set({ error: errorMessage, loading: false });
    }
  },
}), { name: 'encoders-store' }));
